import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import {
    useTable,
    useBlockLayout,
    Cell,
    HeaderGroup,
    useGlobalFilter,
    useSortBy,
    useRowSelect,
    Row,
} from 'react-table';
import { FixedSizeList } from 'react-window';
import { intervalToDuration, formatDuration } from 'date-fns';
import cx from 'classnames';
import * as naturalCompare from 'string-natural-compare';
import { gql, useLazyQuery, useMutation, useQuery } from '@apollo/client';
import { Link, useHistory } from 'react-router-dom';
import axios, { AxiosResponse } from 'axios';

import {
    Button,
    CheckboxCell,
    DateCell,
    DetectionStatusMeterCell,
    GlobalFilter,
    LoadingEllipses,
    SongTitleCell,
    TableHeader,
    TableHeaderGroup,
    InfoTooltipHeader,
} from '@trqk/components';
import {
    createFuzzyTextFilter,
    detectionIsOn,
    getDetectionStatusUpdate,
    getCreatedAtFromId,
    getPlanTypeLabel,
    getScrollbarWidth,
    getWebpackEnvVar,
    locale,
} from '@trqk/utils';
import { queryStrings } from '@trqk/resolvers';

import {
    rowHeight,
    defaultColumnWidth,
    modalFooterHeight,
    tableHeight,
    COLUMN_IDS,
} from '../../constants';
import {
    DetectionStartCell,
    DurationCell,
    ToggleDetectionStatusCell,
} from './cell';
import { DetectionsEnabledHeader } from './header';
import { DeleteAudioWarning, GapAnalysisUnavailable } from './cards';
import { getUpdates, getUpdateWarnings } from '../../helpers';
import { useToast, useTokenModal } from '../../../../../hooks';
import { getWorkDetailPath } from '../../../../../utils';
import { PATHS, HASHES } from '../../../../../constants';

const GET_TRQK_USER = gql`
    query getTrqkUser($id: ID!) {
        getTrqkUser(id: $id) {
            id
            subscription_detection_tracks
        }
    }
`;

interface Props {
    detectionTracks: DetectionTrack[];
    detectionStats: DetectionStats;
    modalOpenedAt: Maybe<number>;
    refresh: Maybe<number>;
    remove(removeModalInput: RemoveModalInput): void;
    setDetectionTracks: SetState<DetectionTrack[]>;
}

const { GET_SESSION_CURRENT_USER, UPDATE_DETECTION_STATUSES } = queryStrings;

/* eslint-disable react/jsx-props-no-spreading */

/**
 * Renders a table of tracks and their detection settings.
 * The total of all column widths should equal the `contentWidth` variable in '../constants.ts'.
 */
export const DetectionTracksTable = ({
    detectionTracks,
    detectionStats,
    modalOpenedAt,
    refresh,
    remove,
    setDetectionTracks,
}: Props): JSX.Element => {
    const trqkDirectoryUrl = getWebpackEnvVar('web', 'TRQK_DIRECTORY_URL');
    const history = useHistory();
    const { add: addToast } = useToast();
    const { getToken } = useTokenModal();
    const [data, setData] = useState(detectionTracks);
    const [updatedData, setUpdatedData] = useState(-1);
    const currentUserQuery = useQuery(GET_SESSION_CURRENT_USER);
    const userId: Maybe<string> =
        currentUserQuery?.data?.session?.currentUser?.id ?? null;

    const [getTrqkUser, getTrqkUserQuery] = useLazyQuery<{
        getTrqkUser: TrqkUser;
    }>(GET_TRQK_USER);

    useEffect(() => {
        if (userId !== null) {
            getTrqkUser({
                variables: {
                    id: userId,
                },
            });
        }
    }, [userId]);

    const subscriptionDetectionTracks =
        getTrqkUserQuery?.data?.getTrqkUser?.subscription_detection_tracks ?? 0;

    // When our cell renderer calls updateData, use the item id, columnId and new value to update
    // the original data
    const skipPageResetRef = useRef(false);

    const updateData = useCallback(
        (nextData: DetectionTrack[]) => {
            skipPageResetRef.current = true;

            setDetectionTracks(nextData);
            setData(nextData);
            setUpdatedData(Date.now());
        },
        [skipPageResetRef],
    );

    useEffect(() => {
        if (refresh !== null) {
            skipPageResetRef.current = true;

            setDetectionTracks(detectionTracks);
            setData(detectionTracks);
        }
    }, [refresh]);

    useEffect(() => {
        skipPageResetRef.current = false;
    });

    const defaultColumn = useMemo(
        () => ({
            width: defaultColumnWidth,
        }),
        [],
    );

    const {
        location: { search },
    } = history;

    const columns = useMemo(
        () => [
            {
                id: COLUMN_IDS.TITLE,
                accessor: ({
                    deleted,
                    audio_id,
                    filename,
                    title,
                    song_id,
                }: DetectionTrack): {
                    audioDeleted: boolean;
                    filename: Maybe<string>;
                    href: Maybe<string>;
                    isNew: boolean;
                    title: Maybe<string>;
                } => {
                    const audioDeleted = deleted ?? false;

                    const href = getWorkDetailPath({
                        audioDeleted,
                        search,
                        trqkAudioId: audio_id,
                        trqkSongId: song_id ?? null,
                    });

                    const createdAt = getCreatedAtFromId(audio_id);
                    const isNew =
                        modalOpenedAt !== null &&
                        createdAt !== null &&
                        createdAt > modalOpenedAt;

                    return {
                        audioDeleted,
                        filename,
                        href,
                        isNew,
                        title,
                    };
                },
                Cell: SongTitleCell,
                headerLabel: 'Work Title / Audio',
                Header: TableHeader,
                width: 255,
                sortType: (
                    rowA: Row<DetectionTrack>,
                    rowB: Row<DetectionTrack>,
                ): number =>
                    naturalCompare(
                        rowA?.original?.filename ?? '',
                        rowB?.original?.filename ?? '',
                    ),
            },
            {
                id: COLUMN_IDS.UPLOAD_DATE,
                accessor: ({ audio_id }: DetectionTrack): Maybe<number> =>
                    getCreatedAtFromId(audio_id),
                Cell: DateCell,
                headerLabel: 'Upload Date',
                Header: TableHeader,
                width: 110,
            },
            {
                id: COLUMN_IDS.ENABLED,
                accessor: ({
                    audio_id,
                    detection_history,
                    detection_status,
                }: DetectionTrack): {
                    detectionHistory: ManageDetectionsStatusChange[];
                    detectionStatus: Maybe<DetectionStatus>;
                    trqkAudioId: Maybe<string>;
                } => ({
                    detectionHistory: detection_history ?? [],
                    detectionStatus: detection_status ?? null,
                    trqkAudioId: audio_id ?? null,
                }),
                updateData,
                data,
                updatedData,
                enabledCountNonDraftOnly: detectionStats.enabled.nonDraftOnly,
                enabledCountAll: detectionStats.enabled.all,
                subscriptionDetectionTracks,
                Cell: ToggleDetectionStatusCell,
                Header: DetectionsEnabledHeader,
                width: 120,
                height: rowHeight,
            },
            {
                id: COLUMN_IDS.DETECTION_START,
                accessor: ({
                    detection_history,
                }: DetectionTrack): {
                    detectionStartDate: Date | null;
                    draft: boolean;
                } => {
                    const statusUpdate = getDetectionStatusUpdate({
                        detectionHistory: detection_history,
                    });

                    let detectionStartDate = null;

                    if (
                        statusUpdate !== null &&
                        detectionIsOn(statusUpdate?.status)
                    ) {
                        detectionStartDate = new Date(statusUpdate.timestamp);
                    }

                    return {
                        detectionStartDate,
                        draft: statusUpdate?.draft ?? false,
                    };
                },
                Cell: DetectionStartCell,
                headerLabel: 'Start Date',
                headerInfo:
                    locale.enUS.MANAGE_DETECTIONS.DETECTION_SETTINGS
                        .START_DATE_INFO,
                Header: InfoTooltipHeader,
                tooltipMinWidth: 350,
                tooltipMaxWidth: 350,
                width: 120,
                sortType: (
                    rowA: Row<DetectionTrack>,
                    rowB: Row<DetectionTrack>,
                ): number => {
                    const detectionStartA = new Date(
                        rowA?.values?.detection_start.detectionStartDate ?? 0,
                    );

                    const detectionStartB = new Date(
                        rowB?.values?.detection_start.detectionStartDate ?? 0,
                    );

                    if (detectionStartA < detectionStartB) {
                        return 1;
                    }

                    if (detectionStartA > detectionStartB) {
                        return -1;
                    }

                    return 0;
                },
            },
            {
                id: COLUMN_IDS.DURATION,
                accessor: ({
                    detection_history,
                }: DetectionTrack): {
                    label: Maybe<string>;
                    draft: boolean;
                } => {
                    const statusUpdate = getDetectionStatusUpdate({
                        detectionHistory: detection_history,
                    });

                    let detectionStartDate = null;

                    if (
                        statusUpdate !== null &&
                        detectionIsOn(statusUpdate?.status)
                    ) {
                        detectionStartDate = new Date(statusUpdate.timestamp);
                    }

                    let label = '0 days';

                    if (detectionStartDate !== null) {
                        const end = new Date();
                        const duration = intervalToDuration({
                            start: detectionStartDate,
                            end,
                        });

                        label = formatDuration(duration, {
                            format: ['months', 'days'],
                        });
                    }

                    return {
                        label,
                        draft: statusUpdate?.draft ?? false,
                    };
                },
                Cell: DurationCell,
                headerLabel: 'Duration',
                headerInfo:
                    locale.enUS.MANAGE_DETECTIONS.DETECTION_SETTINGS
                        .DURATION_INFO,
                Header: InfoTooltipHeader,
                tooltipMinWidth: 500,
                tooltipMaxWidth: 500,
                width: 100,
            },
            {
                id: COLUMN_IDS.DETECTION_HISTORY,
                accessor: ({
                    detection_history,
                }: DetectionTrack): DetectionStatusChange[] | null =>
                    detection_history ?? null,
                Cell: DetectionStatusMeterCell,
                headerLabel: '% of Current Quarter',
                Header: TableHeader,
                width: 180,
            },
            {
                id: COLUMN_IDS.SELECTION,
                Cell: CheckboxCell,
                width: 25,
                accessor: () => null,
            },
        ],
        [
            updateData,
            data,
            updatedData,
            detectionStats.enabled.nonDraftOnly,
            detectionStats.enabled.all,
            search,
            subscriptionDetectionTracks,
        ],
    );

    const filterTypes = useMemo(
        () => ({
            globalFilter: createFuzzyTextFilter<DetectionTrack>({
                keys: [
                    `values.${COLUMN_IDS.TITLE}.title`,
                    `values.${COLUMN_IDS.TITLE}.filename`,
                ],
            }),
        }),
        [],
    );

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        rows,
        prepareRow,
        setGlobalFilter,
        state,
        toggleAllRowsSelected,
        totalColumnsWidth,
    } = useTable<DetectionTrack>(
        {
            columns,
            data,
            defaultColumn,
            filterTypes,
            globalFilter: filterTypes.globalFilter,
            initialState: {
                sortBy: [
                    {
                        id: COLUMN_IDS.UPLOAD_DATE,
                        desc: true,
                    },
                    {
                        id: COLUMN_IDS.TITLE,
                        desc: false,
                    },
                ],
            },
            autoResetPage: !skipPageResetRef.current,
            autoResetExpanded: !skipPageResetRef.current,
            autoResetGroupBy: !skipPageResetRef.current,
            autoResetSelectedRows: !skipPageResetRef.current,
            autoResetSortBy: !skipPageResetRef.current,
            autoResetFilters: !skipPageResetRef.current,
            autoResetGlobalFilter: false,
            autoResetRowState: !skipPageResetRef.current,
        },
        useBlockLayout,
        useGlobalFilter,
        useSortBy,
        useRowSelect,
    );

    const RenderRow = useCallback(
        ({ index, style }) => {
            const row = rows[index];

            prepareRow(row);

            const ariaRowIndex = index + 1;
            const evenRow = ariaRowIndex % 2 === 0;
            const rowProps = row.getRowProps();
            const { key: rowKey, role } = rowProps;

            const classNames = cx(
                'tr relative flex items-center respond-to-mouse',
                {
                    'even-row': evenRow,
                },
            );

            return (
                <div
                    key={rowKey}
                    role={role}
                    aria-rowindex={ariaRowIndex}
                    className={classNames}
                    style={{
                        ...style,
                        height: rowHeight,
                    }}
                >
                    {row.cells.map((cell: Cell<DetectionTrack>) => {
                        const { key, ...rest } = cell.getCellProps();

                        return (
                            <div key={key} {...rest} className="td text-sm">
                                {cell.render('Cell')}
                            </div>
                        );
                    })}
                </div>
            );
        },
        [
            prepareRow,
            rows,
            // We need to list state.selectedRowIds as a dependency here to ensure that the checkbox
            // is updated when the user selects a row.
            Object.keys(state.selectedRowIds).length,
            updatedData,
        ],
    );

    const updates = getUpdates(data);

    const [updateDetectionStatuses, { loading }] = useMutation(
        UPDATE_DETECTION_STATUSES,
        {
            onCompleted: result => {
                const results =
                    result?.updateDetectionStatuses?.results ?? null;

                if (results !== null) {
                    // Update global detectionTracks and table data with results of all successful
                    // updateTrqkAudio mutations
                    updateData(
                        data.map(
                            (
                                detectionTrack: DetectionTrack,
                            ): DetectionTrack => {
                                const match = results.find(
                                    (
                                        target: PromiseSettledResult<TrqkAudio>,
                                    ): boolean =>
                                        target.status === 'fulfilled' &&
                                        target.value !== null &&
                                        target?.value.id ===
                                            detectionTrack.audio_id,
                                );

                                const detectionStatus: DetectionStatus | null =
                                    match?.value?.detection_status ?? null;
                                const detectionHistory:
                                    | DetectionStatusChange[]
                                    | null =
                                    match?.value?.detection_history ?? null;

                                if (
                                    detectionStatus !== null &&
                                    detectionHistory !== null
                                ) {
                                    return {
                                        ...detectionTrack,
                                        detection_status:
                                            match.value.detection_status,
                                        detection_history:
                                            match.value.detection_history,
                                    };
                                }

                                return detectionTrack;
                            },
                        ),
                    );

                    const failures = results.filter(
                        ({
                            status,
                        }: PromiseSettledResult<TrqkAudio>): boolean =>
                            status === 'rejected',
                    );

                    if (failures.length === 0) {
                        addToast({
                            kind: 'info-banner',
                            content:
                                locale.enUS.MANAGE_DETECTIONS.TOASTS
                                    .UPDATES_SUCCEEDED,
                        });

                        const didEnableDetection = updates.some(
                            (update: ManageDetectionsUpdate): boolean =>
                                detectionIsOn(update.statusChange.status),
                        );

                        remove({
                            success: true,
                            canceled: false,
                            output: {
                                didEnableDetection,
                            },
                        });

                        history.push(PATHS.DETECTIONS);
                    } else {
                        // We're not removing the modal if there was one or more failures -
                        // instead, we: 1) show a toast warning
                        addToast({
                            kind: 'warning-banner',
                            content:
                                locale.enUS.MANAGE_DETECTIONS.TOASTS
                                    .SOME_UPDATES_FAILED,
                        });

                        // 2): revert any draft data back to its previous state, both in global
                        // detectionTracks and in table data
                        updateData(
                            data.map(
                                (
                                    detectionTrack: DetectionTrack,
                                ): DetectionTrack => {
                                    if (
                                        detectionTrack.detection_history !==
                                        null
                                    ) {
                                        const statusUpdate =
                                            getDetectionStatusUpdate({
                                                detectionHistory:
                                                    detectionTrack.detection_history,
                                                nonDraftOnly: true,
                                            });

                                        const nextDetectionHistory = (
                                            detectionTrack?.detection_history ??
                                            []
                                        ).filter(
                                            ({
                                                draft,
                                            }: ManageDetectionsStatusChange): boolean =>
                                                draft === undefined ||
                                                draft === false,
                                        );

                                        const nextDetectionStatus =
                                            statusUpdate?.status ??
                                            ('DISABLED' as DetectionStatus);

                                        return {
                                            ...detectionTrack,
                                            detection_history:
                                                nextDetectionHistory,
                                            detection_status:
                                                nextDetectionStatus,
                                        };
                                    }

                                    return detectionTrack;
                                },
                            ),
                        );
                    }
                }
            },
            onError: () => {
                addToast({
                    kind: 'warning-banner',
                    content: `${locale.enUS.MANAGE_DETECTIONS.TOASTS.UPDATES_FAILED}`,
                });
            },
        },
    );

    const doUpdates = useCallback((): void => {
        getToken((token: string): void => {
            updateDetectionStatuses({
                variables: {
                    updates,
                    token,
                    trqkDirectoryUrl,
                },
            });
        });
    }, [data, updateDetectionStatuses, getToken, updates]);

    const planTypeLabel = getPlanTypeLabel(detectionStats.planType);

    const enabledStatusLabelDraft = `*${
        locale.enUS.MANAGE_DETECTIONS.DETECTION_SETTINGS.ENABLED_STATUS
            .AFTER_YOU_CLICK_APPLY
    } ${detectionStats.enabled.all ?? 0} ${
        locale.enUS.MANAGE_DETECTIONS.DETECTION_SETTINGS.ENABLED_STATUS.OF_THE
    } ${detectionStats.detectionCapacity ?? 0} ${
        locale.enUS.MANAGE_DETECTIONS.DETECTION_SETTINGS.ENABLED_STATUS
            .AVAILABLE_TRACKS
    } ${planTypeLabel}.`;

    const enabledStatusLabelSaved = `${
        locale.enUS.MANAGE_DETECTIONS.DETECTION_SETTINGS.ENABLED_STATUS
            .YOU_HAVE_ENABLED
    } ${detectionStats.enabled.nonDraftOnly ?? 0} ${
        locale.enUS.MANAGE_DETECTIONS.DETECTION_SETTINGS.ENABLED_STATUS.OF_THE
    } ${detectionStats.detectionCapacity ?? 0} ${
        locale.enUS.MANAGE_DETECTIONS.DETECTION_SETTINGS.ENABLED_STATUS
            .AVAILABLE_TRACKS
    } ${planTypeLabel}.`;

    const enabledStatusLabel =
        detectionStats.enabled.all === detectionStats.enabled.nonDraftOnly
            ? enabledStatusLabelSaved
            : enabledStatusLabelDraft;

    const selectedTrqkAudioIds = Object.entries(state.selectedRowIds).reduce(
        (acc: string[], [rowId, selected]): string[] => {
            if (selected) {
                const row = rows.find(
                    ({ id }: Row<DetectionTrack>): boolean => id === rowId,
                );

                if (row !== undefined) {
                    const trqkAudioId = row.original.audio_id;

                    acc.push(trqkAudioId);
                }
            }

            return acc;
        },
        [],
    );

    const deleteAudioFiles = useCallback((): void => {
        const deletePromises = selectedTrqkAudioIds.map(
            (selectedTrqkAudioId: string): Promise<{ id: string }> =>
                new Promise((resolve, reject) => {
                    getToken((token: string): void => {
                        axios({
                            method: 'post',
                            url: trqkDirectoryUrl,
                            headers: {
                                Authorization: token,
                            },
                            data: {
                                query: `
                                mutation deleteTrqkAudio($input: DeleteTrqkAudioInput!) {
                                    deleteTrqkAudio(input: $input) {
                                        id
                                    }
                                }
                            `,
                                variables: {
                                    input: {
                                        id: selectedTrqkAudioId,
                                    },
                                },
                            },
                        })
                            .then(
                                (
                                    response: AxiosResponse<{
                                        data: {
                                            deleteTrqkAudio: {
                                                id: string;
                                            };
                                        };
                                    }>,
                                ): void => {
                                    resolve(response.data.data.deleteTrqkAudio);
                                },
                            )
                            .catch((error: Error): void => {
                                reject(error);
                            });
                    });
                }),
        );

        Promise.allSettled(deletePromises).then(
            (results: PromiseSettledResult<{ id: string }>[]): void => {
                const deletedIds = results.reduce(
                    (
                        acc: string[],
                        result: PromiseSettledResult<{ id: string }>,
                    ): string[] => {
                        const id =
                            // eslint-disable-next-line -- tsc doesn't like this, but that seems like error in the type def: https://github.com/microsoft/TypeScript/issues/42012
                            // @ts-ignore
                            result?.value?.id ?? null;

                        if (result.status === 'fulfilled' && id !== null) {
                            acc.push(id);
                        }

                        return acc;
                    },
                    [],
                );

                if (deletedIds.length !== selectedTrqkAudioIds.length) {
                    addToast({
                        kind: 'warning-banner',
                        content:
                            locale.enUS.MANAGE_DETECTIONS.TOASTS
                                .SOME_AUDIO_DELETIONS_FAILED,
                    });
                } else if (deletedIds.length === 1) {
                    addToast({
                        kind: 'info-banner',
                        content:
                            locale.enUS.MANAGE_DETECTIONS.TOASTS
                                .AUDIO_DELETIONS_SUCCEEDED_SINGULAR,
                    });
                } else {
                    addToast({
                        kind: 'info-banner',
                        content:
                            locale.enUS.MANAGE_DETECTIONS.TOASTS
                                .AUDIO_DELETIONS_SUCCEEDED_PLURAL,
                    });
                }

                const nextData = data.filter(
                    ({ audio_id }: DetectionTrack): boolean =>
                        !deletedIds.includes(audio_id),
                );

                updateData(nextData);
            },
        );
    }, [data, selectedTrqkAudioIds, getToken]);

    const scrollbarWidth = useMemo(() => getScrollbarWidth(), []);

    return (
        <>
            <div
                className="flex justify-between items-end"
                style={{
                    marginLeft: 40,
                    marginRight: 76,
                    marginBottom: 7,
                }}
            >
                <div className="pb-3" style={{ width: 345 }}>
                    <GlobalFilter
                        globalFilter={state.globalFilter}
                        setGlobalFilter={setGlobalFilter}
                        placeholder={
                            locale.enUS.MANAGE_DETECTIONS.DETECTION_SETTINGS
                                .GLOBAL_FILTER_PLACEHOLDER
                        }
                    />
                </div>
                <div
                    className="flex flex-col items-center"
                    style={{
                        width: 515,
                    }}
                >
                    <div className="font-semibold text-sm">
                        {
                            locale.enUS.MANAGE_DETECTIONS.DETECTION_SETTINGS
                                .DETECTION_COVERAGE
                        }
                    </div>
                    <div
                        className="w-full border-t border-l border-r border-tertiary-300 rounded-tl-lg rounded-tr-lg"
                        style={{
                            height: 10,
                        }}
                    />
                </div>
            </div>
            <div {...getTableProps()} className="trqk-table font-normal mx-10">
                <div>
                    {headerGroups.map(
                        (
                            headerGroup: HeaderGroup<DetectionTrack>,
                        ): JSX.Element => {
                            const { key: headerGroupKey } =
                                headerGroup.getHeaderGroupProps();

                            return (
                                <TableHeaderGroup<DetectionTrack>
                                    key={headerGroupKey}
                                    headerGroup={headerGroup}
                                />
                            );
                        },
                    )}
                </div>

                <div
                    {...getTableBodyProps()}
                    style={{
                        height: tableHeight,
                        width: totalColumnsWidth + scrollbarWidth,
                    }}
                    className="overflow-y-auto overflow-x-hidden respond-to-mouse"
                >
                    <FixedSizeList
                        height={tableHeight}
                        itemCount={rows.length}
                        itemSize={rowHeight}
                        width={totalColumnsWidth + scrollbarWidth}
                    >
                        {RenderRow}
                    </FixedSizeList>
                </div>
            </div>
            <div
                className="relative flex items-start justify-end pt-3 px-10"
                style={{
                    height: modalFooterHeight,
                }}
            >
                <div className="flex-grow mr-4">
                    <p className="text-sm text-tertiary-400">
                        {enabledStatusLabel}{' '}
                        <Link
                            className="text-sm underline text-primary-500 respond-to-mouse"
                            type="button"
                            to={location => ({
                                search: location.search,
                                pathname: PATHS.SETTINGS,
                                hash: HASHES.SETTINGS.PAYMENT_PLAN,
                                state: {
                                    fromLink: true,
                                },
                            })}
                        >
                            {
                                locale.enUS.MANAGE_DETECTIONS.DETECTION_SETTINGS
                                    .ENABLED_STATUS.UPDATE_PAYMENT_PLAN
                            }
                        </Link>
                    </p>
                </div>
                <div
                    className="flex flex-none justify-end"
                    style={{ width: 370 }}
                >
                    {/*
                        No rows are selected for deletion, so we show the normal Cancel / Apply
                        buttons for updating the detection enabled status for each track.
                    */}
                    {selectedTrqkAudioIds.length < 1 && (
                        <>
                            <div
                                className="flex items-center justify-center"
                                style={{ width: 250 }}
                            >
                                <Button
                                    addClasses="respond-to-mouse"
                                    autoWidth={true}
                                    color="danger-tertiary-light-bg"
                                    handleClick={(): void => {
                                        remove({
                                            canceled: true,
                                            success: true,
                                        });
                                        history.push(PATHS.DETECTIONS);
                                    }}
                                    padding="none"
                                >
                                    {
                                        locale.enUS.MANAGE_DETECTIONS.BUTTONS
                                            .CANCEL
                                    }
                                </Button>
                            </div>
                            <Button
                                addClasses="w-full ml-10 respond-to-mouse"
                                color="primary-light-bg"
                                disabled={updates.length < 1}
                                handleClick={(): void => {
                                    const updateWarnings = getUpdateWarnings({
                                        data,
                                        maxDetections:
                                            subscriptionDetectionTracks,
                                    });

                                    if (updateWarnings.gapAnalysisUnavailable) {
                                        addToast({
                                            kind: 'warning-card',
                                            RenderContent:
                                                GapAnalysisUnavailable,
                                            persist: true,
                                            onRemove: (
                                                input?: RemoveToastInput,
                                            ): void => {
                                                const canceled =
                                                    input?.canceled ?? false;

                                                if (!canceled) {
                                                    // If user didn't click "Make Changes" in
                                                    // GapAnalysisUnavailable, make the API calls
                                                    doUpdates();
                                                }
                                            },
                                        });
                                    } else {
                                        // If there were no warning messages, make the API calls
                                        doUpdates();
                                    }
                                }}
                            >
                                <div
                                    className="flex justify-center items-center"
                                    style={{ height: 24 }}
                                >
                                    {!loading && (
                                        <>
                                            {
                                                locale.enUS.MANAGE_DETECTIONS
                                                    .BUTTONS.APPLY
                                            }
                                        </>
                                    )}
                                    {loading && <LoadingEllipses width={40} />}
                                </div>
                            </Button>
                        </>
                    )}
                    {/*
                        One or more rows are selected for deletion, so we show the Clear Selection
                        / Delete buttons for deleting audio files.
                    */}
                    {selectedTrqkAudioIds.length > 0 && (
                        <>
                            <div
                                className="flex items-center justify-center"
                                style={{ width: 250 }}
                            >
                                <Button
                                    addClasses="respond-to-mouse"
                                    autoWidth={true}
                                    color="danger-tertiary-light-bg"
                                    handleClick={(): void => {
                                        toggleAllRowsSelected(false);
                                    }}
                                    padding="none"
                                >
                                    {
                                        locale.enUS.MANAGE_DETECTIONS.BUTTONS
                                            .CLEAR_SELECTION
                                    }
                                </Button>
                            </div>
                            <Button
                                addClasses="w-full ml-10 respond-to-mouse"
                                color="danger-primary-light-bg"
                                handleClick={(): void => {
                                    addToast({
                                        kind: 'warning-card',
                                        renderContentInput: {
                                            selectedFiles:
                                                selectedTrqkAudioIds.length,
                                        },
                                        RenderContent: DeleteAudioWarning,
                                        persist: true,
                                        onRemove: (
                                            input?: RemoveToastInput,
                                        ): void => {
                                            const success =
                                                input?.success ?? false;

                                            if (success) {
                                                deleteAudioFiles();
                                            }
                                        },
                                    });
                                }}
                            >
                                <div
                                    className="flex justify-center items-center"
                                    style={{ height: 24 }}
                                >
                                    {!loading && (
                                        <>
                                            {
                                                locale.enUS.MANAGE_DETECTIONS
                                                    .BUTTONS.DELETE_AUDIO
                                            }{' '}
                                            {`(${selectedTrqkAudioIds.length})`}
                                        </>
                                    )}
                                    {loading && <LoadingEllipses width={40} />}
                                </div>
                            </Button>
                        </>
                    )}
                </div>
            </div>
        </>
    );
};
